import PropTypes from "prop-types";
import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cropper from "react-easy-crop";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Maximize2,
  Minimize2,
  Upload,
  Image as ImageIcon,
  RotateCcw,
} from "lucide-react";
import AppLayout from "@components/AppLayout";
import client, { BASE_URL } from "../../src/api/client";
import { ADDRESS_DATA, getBarangays } from "../../src/data/luzonAddressData";
import { fileToDataUrl, getCroppedImageDataUrl } from "../../src/utils/imageCrop";
import { resolveBackendAssetUrl } from "../../src/utils/assetUrl";
import "../styles/membership-form.css";
import "../styles/membership-form-wizard.css";

// --- Constants & Options ---

const genderOptions = [
  { label: "Man", value: "Male" },
  { label: "Woman", value: "Female" },
  { label: "Other (please state)", value: "Other" },
  { label: "Prefer not to say", value: "Prefer not to say" },
];

const inputValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const normalizeDateForInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().split("T")[0];
};

const inferRegionFromProvince = (province) => {
  if (!province) return "";
  for (const [region, provinces] of Object.entries(ADDRESS_DATA)) {
    if (Object.keys(provinces).includes(province)) {
      return region;
    }
  }
  return "";
};

const buildInitialFormData = (user) => {
  const region = user?.region || inferRegionFromProvince(user?.province) || "";

  return {
    firstName: inputValue(user?.firstName),
    middleInitial: inputValue(user?.middleInitial),
    lastName: inputValue(user?.lastName),
    dateOfBirth: normalizeDateForInput(user?.dateOfBirth),
    placeOfBirth: inputValue(user?.placeOfBirth),

    region,
    province: inputValue(user?.province),
    city: inputValue(user?.city || user?.municipality),
    barangay: inputValue(user?.barangay || user?.address_line2 || user?.addressLine2),
    addressLine1: inputValue(user?.addressLine1 || user?.address_line1),
    zipCode: inputValue(user?.zipCode || user?.zip_code || user?.postalCode || user?.postal_code),

    education: inputValue(user?.education),
    maritalStatus: inputValue(user?.maritalStatus),
    numberOfChildren: inputValue(user?.childrenCount ?? user?.numberOfChildren),
    religion: inputValue(user?.religion),
    phone: inputValue(user?.phone),
    email: inputValue(user?.email),
    gender: inputValue(user?.gender),
    otherGender: inputValue(user?.otherGender),

    company: inputValue(user?.company),
    position: inputValue(user?.position),
    yearsEmployed: inputValue(user?.yearsEmployed),
    department: inputValue(user?.department),
    unionAffiliation: inputValue(user?.unionAffiliation),
    unionPosition: inputValue(user?.unionPosition),
    membershipDate: inputValue(user?.membershipDate || new Date().toISOString().split("T")[0]),

    emergencyName: inputValue(user?.emergencyContact?.name || user?.emergencyContactName),
    emergencyRelationship: inputValue(user?.emergencyContact?.relationship || user?.emergencyContactRelationship),
    emergencyAddress: inputValue(user?.emergencyContact?.address || user?.emergencyContactAddress),
    emergencyPhone: inputValue(user?.emergencyContact?.phone || user?.emergencyContactPhone),
  };
};

const hasValue = (value) => String(value ?? "").trim().length > 0;
const displayValue = (value) => (hasValue(value) ? String(value).trim() : "—");

const formatYearsEmployed = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return value;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;

  const amount = Number.isInteger(numeric)
    ? String(numeric)
    : String(parseFloat(numeric.toFixed(2)));
  const unit = Math.abs(numeric) === 1 ? "year" : "years";
  return `${amount} ${unit}`;
};
const PHOTO_CROP_FRAME_RATIO = 1;
const MOBILE_SIGNATURE_BREAKPOINT = 768;

const buildAddressText = (formState) => {
  const addressParts = [
    formState.addressLine1,
    formState.barangay,
    formState.city,
    formState.province,
    formState.region,
    formState.zipCode,
  ];

  return addressParts
    .map((part) => String(part ?? "").trim())
    .filter((part) => part.length > 0)
    .join(", ");
};

const renderReviewRow = (label, value) => (
  <div className="review-data-row">
    <strong>{label}</strong>
    <span>:</span>
    <span>{displayValue(value)}</span>
  </div>
);

const computeMinimumCropZoom = (width, height) => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return 1;
  }

  const aspectRatio = width / height;
  const zoomRequired = PHOTO_CROP_FRAME_RATIO * aspectRatio;
  return Number(Math.max(1, zoomRequired).toFixed(2));
};

const resolvePhotoPreviewUrl = (value) => (
  resolveBackendAssetUrl(value, { apiBaseUrl: BASE_URL }) ?? null
);

// --- Helper Components ---

function FormSection({ title, children, isActive }) {
  if (!isActive) return null;
  return (
    <section className="membership-form__section animate-fade-in">
      <div className="membership-form__section-banner">{title}</div>
      {children}
    </section>
  );
}

function SignaturePad({ initialValue, onSave }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!initialValue);
  const [signatureImg, setSignatureImg] = useState(initialValue);
  const [isPadMaximized, setIsPadMaximized] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= MOBILE_SIGNATURE_BREAKPOINT;
  });

  const updateViewportState = useCallback(() => {
    if (typeof window === "undefined") return;
    setIsMobileViewport(window.innerWidth <= MOBILE_SIGNATURE_BREAKPOINT);
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || signatureImg) return;

    const wrapper = canvas.parentElement;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const nextWidth = Math.max(1, Math.floor(rect.width));
    const nextHeight = Math.max(1, Math.floor(rect.height));
    const dpr = Math.max(window.devicePixelRatio || 1, 1);

    if (canvas.width === Math.floor(nextWidth * dpr) && canvas.height === Math.floor(nextHeight * dpr)) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.strokeStyle = "#000";
      }
      return;
    }

    const snapshot = hasSignature ? canvas.toDataURL("image/png") : null;

    canvas.width = Math.floor(nextWidth * dpr);
    canvas.height = Math.floor(nextHeight * dpr);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";

    if (snapshot) {
      const image = new Image();
      image.onload = () => {
        ctx.drawImage(image, 0, 0, nextWidth, nextHeight);
      };
      image.src = snapshot;
    }
  }, [hasSignature, signatureImg]);
  
  // Update local state if initialValue changes (e.g. loaded asynchronously)
  useEffect(() => {
    if (initialValue) {
        setSignatureImg(initialValue);
        setHasSignature(true);
    }
  }, [initialValue]);

  useEffect(() => {
    updateViewportState();
    resizeCanvas();

    window.addEventListener("resize", updateViewportState);
    window.addEventListener("resize", resizeCanvas);
    return () => {
      window.removeEventListener("resize", updateViewportState);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [updateViewportState, resizeCanvas]);

  useEffect(() => {
    resizeCanvas();
  }, [isMobileViewport, isPadMaximized, signatureImg, resizeCanvas]);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsPadMaximized(false);
    }
  }, [isMobileViewport]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    if (signatureImg) return;
    if (e.cancelable) e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || signatureImg) return;
    if (e.cancelable) e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (hasSignature && !signatureImg) {
      onSave(exportNormalizedSignature());
    }
  };

  const exportNormalizedSignature = () => {
    const sourceCanvas = canvasRef.current;
    if (!sourceCanvas) return null;

    const srcCtx = sourceCanvas.getContext("2d");
    if (!srcCtx) return sourceCanvas.toDataURL();

    const { width: srcWidth, height: srcHeight } = sourceCanvas;
    const imageData = srcCtx.getImageData(0, 0, srcWidth, srcHeight).data;

    let minX = srcWidth;
    let minY = srcHeight;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < srcHeight; y += 1) {
      for (let x = 0; x < srcWidth; x += 1) {
        const alpha = imageData[(y * srcWidth + x) * 4 + 3];
        if (alpha > 0) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) {
      return sourceCanvas.toDataURL();
    }

    const inkWidth = maxX - minX + 1;
    const inkHeight = maxY - minY + 1;

    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = inkWidth;
    cropCanvas.height = inkHeight;
    const cropCtx = cropCanvas.getContext("2d");
    if (!cropCtx) return sourceCanvas.toDataURL();
    cropCtx.drawImage(sourceCanvas, minX, minY, inkWidth, inkHeight, 0, 0, inkWidth, inkHeight);

    const shouldRotateForMobile = isMobileViewport && inkHeight > inkWidth;
    let orientedCanvas = cropCanvas;

    if (shouldRotateForMobile) {
      const rotatedCanvas = document.createElement("canvas");
      rotatedCanvas.width = inkHeight;
      rotatedCanvas.height = inkWidth;
      const rotatedCtx = rotatedCanvas.getContext("2d");
      if (rotatedCtx) {
        rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
        rotatedCtx.rotate(-Math.PI / 2);
        rotatedCtx.drawImage(cropCanvas, -inkWidth / 2, -inkHeight / 2);
        orientedCanvas = rotatedCanvas;
      }
    }

    const outputCanvas = document.createElement("canvas");
    outputCanvas.width = 600;
    outputCanvas.height = 240;
    const outputCtx = outputCanvas.getContext("2d");
    if (!outputCtx) return sourceCanvas.toDataURL();

    const padding = 30;
    const availWidth = outputCanvas.width - padding * 2;
    const availHeight = outputCanvas.height - padding * 2;
    const fittedScale = Math.min(
      availWidth / orientedCanvas.width,
      availHeight / orientedCanvas.height
    );
    const scale = fittedScale * 0.5;
    const drawWidth = orientedCanvas.width * scale;
    const drawHeight = orientedCanvas.height * scale;
    const drawX = (outputCanvas.width - drawWidth) / 2;
    const drawY = (outputCanvas.height - drawHeight) / 2;

    outputCtx.drawImage(orientedCanvas, drawX, drawY, drawWidth, drawHeight);
    return outputCanvas.toDataURL();
  };

  const clear = () => {
    if (signatureImg) {
      setSignatureImg(null);
      setHasSignature(false);
      onSave(null);
    } else {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
      onSave(null);
    }
  };

  if (signatureImg) {
    return (
      <div className="signature-pad-container">
        {isMobileViewport && (
          <p className="signature-mobile-hint">Rotate your device for easier signing.</p>
        )}
        <label className="membership-form__label" style={{marginBottom: '8px', display: 'block', fontWeight: 500}}>Current Signature:</label>
        <div className="signature-pad-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px' }}>
          <img src={signatureImg} alt="Saved Signature" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} />
        </div>
        <div className="signature-actions">
           <button type="button" onClick={clear} className="button-outline-sm">
            <RotateCcw size={14} /> Clear & Sign Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`signature-pad-container${isMobileViewport && isPadMaximized ? " signature-pad-container--maximized" : ""}`}>
      {isMobileViewport && (
        <p className="signature-mobile-hint">Rotate your device for easier signing.</p>
      )}
      <div className={`signature-pad-wrapper${isMobileViewport ? " signature-pad-wrapper--mobile" : ""}${isMobileViewport && isPadMaximized ? " signature-pad-wrapper--expanded" : ""}`}>
        <canvas
          ref={canvasRef}
          className="signature-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="signature-placeholder">Sign here</div>
      </div>
      <div className="signature-actions">
        {isMobileViewport && (
          <button
            type="button"
            onClick={() => setIsPadMaximized((prev) => !prev)}
            className="button-outline-sm signature-action-icon-btn"
            aria-label={isPadMaximized ? "Minimize signature pad" : "Maximize signature pad"}
            title={isPadMaximized ? "Minimize" : "Maximize"}
          >
            {isPadMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        )}
        <button
          type="button"
          onClick={clear}
          className={`button-outline-sm${isMobileViewport ? " signature-action-icon-btn" : ""}`}
          aria-label="Clear signature"
          title="Clear"
        >
          <RotateCcw size={14} />
          {!isMobileViewport && " Clear"}
        </button>
      </div>
    </div>
  );
}

// --- Main Component ---

export default function MembershipFormPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const totalSteps = 6;
   
  const isReapplication = user?.status === "rejected" || location.state?.isReapplication;
  
  // Initialize form state from user prop
  const [formData, setFormData] = useState(() => buildInitialFormData(user));

  const [photoPreview, setPhotoPreview] = useState(() => resolvePhotoPreviewUrl(user?.profilePicture));
  const [croppedPhotoData, setCroppedPhotoData] = useState(null);
  const [photoCropSource, setPhotoCropSource] = useState(null);
  const [photoCrop, setPhotoCrop] = useState({ x: 0, y: 0 });
  const photoCropperStageRef = useRef(null);
  const [photoCropSize, setPhotoCropSize] = useState(undefined);
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoMinZoom, setPhotoMinZoom] = useState(1);
  const [photoCroppedAreaPixels, setPhotoCroppedAreaPixels] = useState(null);
  const [photoCropError, setPhotoCropError] = useState("");
  const [isApplyingPhotoCrop, setIsApplyingPhotoCrop] = useState(false);
  const [isValidatingPhotoHuman, setIsValidatingPhotoHuman] = useState(false);
  const [, setIsPhotoHumanValidated] = useState(() => Boolean(resolvePhotoPreviewUrl(user?.profilePicture)));
  const [isPhotoCropInteracting, setIsPhotoCropInteracting] = useState(false);
  const [useSameAddress, setUseSameAddress] = useState(false);
  const [signatureData, setSignatureData] = useState(user?.signature || null);
  const [privacyConfirmed, setPrivacyConfirmed] = useState(false);
  const photoMaxZoom = Math.max(3, Number((photoMinZoom + 2).toFixed(2)));

  useEffect(() => {
    setFormData(buildInitialFormData(user));
    setPhotoPreview(resolvePhotoPreviewUrl(user?.profilePicture));
    setCroppedPhotoData(null);
    setPhotoCropSource(null);
    setPhotoCrop({ x: 0, y: 0 });
    setPhotoZoom(1);
    setPhotoMinZoom(1);
    setPhotoCroppedAreaPixels(null);
    setPhotoCropError("");
    setIsValidatingPhotoHuman(false);
    setIsPhotoHumanValidated(Boolean(resolvePhotoPreviewUrl(user?.profilePicture)));
    setIsPhotoCropInteracting(false);
    setUseSameAddress(false);
    setSignatureData(user?.signature || null);
    setPrivacyConfirmed(false);
  }, [user]);

  useEffect(() => {
    if (!useSameAddress) return;

    setFormData((prev) => {
      const mirroredAddress = buildAddressText(prev);
      if (prev.emergencyAddress === mirroredAddress) return prev;
      return { ...prev, emergencyAddress: mirroredAddress };
    });
  }, [
    useSameAddress,
    formData.addressLine1,
    formData.barangay,
    formData.city,
    formData.province,
    formData.region,
    formData.zipCode,
  ]);

  useEffect(() => {
    if (!photoCropSource) {
      setPhotoCropSize(undefined);
      return undefined;
    }

    const stageElement = photoCropperStageRef.current;
    if (!stageElement) return undefined;

    const syncCropSize = () => {
      const width = Math.floor(stageElement.clientWidth);
      const height = Math.floor(stageElement.clientHeight);
      if (width > 0 && height > 0) {
        const size = Math.min(width, height);
        setPhotoCropSize({ width: size, height: size });
      }
    };

    syncCropSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", syncCropSize);
      return () => window.removeEventListener("resize", syncCropSize);
    }

    const resizeObserver = new ResizeObserver(syncCropSize);
    resizeObserver.observe(stageElement);

    return () => resizeObserver.disconnect();
  }, [photoCropSource]);

  useEffect(() => {
    if (!isPhotoCropInteracting) return undefined;

    const stopInteraction = () => setIsPhotoCropInteracting(false);
    window.addEventListener("pointerup", stopInteraction);
    window.addEventListener("pointercancel", stopInteraction);

    return () => {
      window.removeEventListener("pointerup", stopInteraction);
      window.removeEventListener("pointercancel", stopInteraction);
    };
  }, [isPhotoCropInteracting]);

  const isStepValid = useMemo(() => {
    if (currentStep === 1) {
      const requiredFields = [
        formData.firstName,
        formData.lastName,
        formData.dateOfBirth,
        formData.placeOfBirth,
        formData.region,
        formData.province,
        formData.city,
        formData.barangay,
        formData.addressLine1,
        formData.zipCode,
        formData.education,
        formData.maritalStatus,
        formData.numberOfChildren,
        formData.religion,
        formData.phone,
        formData.email,
        formData.gender,
      ];

      if (formData.gender === "Other" && !hasValue(formData.otherGender)) {
        return false;
      }

      return requiredFields.every(hasValue);
    }

    if (currentStep === 2) {
      return [
        formData.company,
        formData.position,
        formData.yearsEmployed,
        formData.unionAffiliation,
        formData.unionPosition,
      ].every(hasValue);
    }

    if (currentStep === 3) {
      return [
        formData.emergencyName,
        formData.emergencyRelationship,
        formData.emergencyAddress,
        formData.emergencyPhone,
      ].every(hasValue);
    }

    if (currentStep === 4) {
      return Boolean(photoPreview);
    }

    if (currentStep === 5) {
      return Boolean(signatureData);
    }

    if (currentStep === 6) {
      return Boolean(privacyConfirmed);
    }

    return true;
  }, [currentStep, formData, photoPreview, signatureData, privacyConfirmed]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (useSameAddress && ["addressLine1", "zipCode"].includes(name)) {
        next.emergencyAddress = buildAddressText(next);
      }
      return next;
    });
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let updates = { [name]: value };
      
      if (name === "region") {
        updates.province = "";
        updates.city = "";
        updates.barangay = "";
      } else if (name === "province") {
        updates.city = "";
        updates.barangay = "";
      } else if (name === "city") {
        updates.barangay = "";
      }
      
      const next = { ...prev, ...updates };
      if (useSameAddress) {
        next.emergencyAddress = buildAddressText(next);
      }
      return next;
    });
  };


  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const imageSource = await fileToDataUrl(file);
        setPhotoCropSource(imageSource);
        setPhotoCrop({ x: 0, y: 0 });
        setPhotoZoom(1);
        setPhotoMinZoom(1);
        setPhotoCroppedAreaPixels(null);
        setPhotoCropError("");
        setIsPhotoHumanValidated(false);
        setIsPhotoCropInteracting(false);
      } catch (error) {
        setPhotoCropError("Unable to load the selected photo. Please try another file.");
      }
    }

    e.target.value = "";
  };

  const handlePhotoCropComplete = (_, croppedAreaPixels) => {
    setPhotoCroppedAreaPixels(croppedAreaPixels);
  };

  const handlePhotoMediaLoaded = (mediaSize) => {
    const mediaWidth = mediaSize?.naturalWidth ?? mediaSize?.width;
    const mediaHeight = mediaSize?.naturalHeight ?? mediaSize?.height;
    const nextMinZoom = computeMinimumCropZoom(mediaWidth, mediaHeight);

    setPhotoMinZoom(nextMinZoom);
    setPhotoZoom((currentZoom) => Math.max(currentZoom, nextMinZoom));
  };

  const validateHumanPhoto = async (photoDataUrl) => {
    if (!photoDataUrl || typeof photoDataUrl !== "string" || !photoDataUrl.startsWith("data:image")) {
      throw new Error("Please upload and crop a valid photo first.");
    }

    setIsValidatingPhotoHuman(true);
    const validateResponse = await fetch(`${BASE_URL}/api/users/${user.id}/validate-avatar-human`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ profilePicture: photoDataUrl }),
    });

    const validateData = await validateResponse.json().catch(() => ({}));
    if (!validateResponse.ok) {
      throw new Error(validateData.error || validateData.message || "Unable to validate profile photo.");
    }

    if (!validateData.human_detected) {
      throw new Error("Please upload a photo that clearly shows a human face.");
    }
  };

  const applyPhotoCrop = async () => {
    if (!photoCropSource || !photoCroppedAreaPixels) {
      setPhotoCropError("Adjust and confirm the crop before continuing.");
      return;
    }

    try {
      setIsApplyingPhotoCrop(true);
      const croppedPhotoDataUrl = await getCroppedImageDataUrl(
        photoCropSource,
        photoCroppedAreaPixels,
        600,
        "image/jpeg",
        0.92
      );

      await validateHumanPhoto(croppedPhotoDataUrl);

      setPhotoPreview(croppedPhotoDataUrl);
      setCroppedPhotoData(croppedPhotoDataUrl);
      setPhotoCropSource(null);
      setPhotoMinZoom(1);
      setPhotoCropError("");
      setIsPhotoHumanValidated(true);
      setIsPhotoCropInteracting(false);
    } catch (error) {
      setPhotoCropError(error?.message || "Unable to apply crop. Please try again.");
      setIsPhotoHumanValidated(false);
    } finally {
      setIsApplyingPhotoCrop(false);
      setIsValidatingPhotoHuman(false);
    }
  };

  const handleNext = () => {
    if (!isStepValid) return;

    if (currentStep === 4) {
      if (photoCropSource) {
        setPhotoCropError("Please apply the crop before continuing.");
        return;
      }

      if (!photoPreview) {
        setPhotoCropError("Please upload and crop your ID photo before continuing.");
        return;
      }
    }

    window.scrollTo(0, 0);
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    window.scrollTo(0, 0);
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!privacyConfirmed) {
      alert("Please confirm the privacy consent before submitting.");
      return;
    }

    setIsSubmitting(true);
    try {
        let finalSignature = signatureData;
        
        const payload = {
            id: user.id,
            ...formData,
        photo: croppedPhotoData,
            signature: finalSignature
        };

        if (isReapplication) {
             await client.post('/api/auth/reapply', payload);
             alert("Application Resubmitted Successfully!");
        } else {
             // For regular approved users updating their profile
             // This might require a different endpoint or permission
             // For now, we alert as this page is mainly for viewing or initial submission logic
             alert("Profile update submitted for review.");
             // await client.put(`/users/${user.id}`, payload); 
        }
        
        navigate("/dashboard");
        window.location.reload(); 
    } catch (error) {
        console.error(error);
        const serverError = error.response?.data?.error;
        const serverMessage = error.response?.data?.message;
        const displayMessage = serverError ? `${serverMessage}: ${serverError}` : (serverMessage || error.message);
        alert("Submission failed: " + displayMessage);
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <AppLayout title="Membership Application" user={user} onLogout={onLogout}>
      <div className="membership-form">
        <div className="membership-form__container">
          
          <header className="membership-form__header">
            <button
              type="button"
              className="membership-form__back"
              onClick={() => {
                  if(currentStep > 1) handleBack();
                  else navigate("/dashboard");
              }}
            >
              <ArrowLeft size={16} />
              {currentStep > 1 ? "Previous Step" : "Back to Dashboard"}
            </button>
            <div className="membership-form__heading">
              <h1>Membership Application</h1>
              <p>Step {currentStep} of {totalSteps}</p>
            </div>
          </header>

          <div className="step-progress-bar">
            {[1, 2, 3, 4, 5, 6].map((s) => (
                <div key={s} className={`step-indicator ${s <= currentStep ? 'active' : ''}`} />
            ))}
          </div>

          <div className="membership-form__card">
            
            <form
              onSubmit={(e) => e.preventDefault()}
              className={`membership-form__wizard-form${currentStep === 6 ? " membership-form__wizard-form--review" : ""}`}
            >
            
            {/* --- STEP 1: PERSONAL INFORMATION --- */}
            <FormSection title="Personal Information" isActive={currentStep === 1}>
              <div className="membership-form__fields membership-form__fields--cols-2">
                <label className="membership-form__field">
                  <span>First Name</span>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="Enter first name" />
                </label>
                <label className="membership-form__field">
                  <span>Last Name</span>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Enter last name" />
                </label>
              </div>

               <div className="membership-form__fields membership-form__fields--cols-3">
                 <label className="membership-form__field">
                  <span>Middle Name</span>
                  <input type="text" name="middleInitial" value={formData.middleInitial} onChange={handleInputChange} placeholder="Middle Name" />
                </label>
                <label className="membership-form__field">
                  <span>Date of Birth</span>
                  <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} />
                </label>
                 <label className="membership-form__field">
                  <span>Place of Birth</span>
                  <input type="text" name="placeOfBirth" value={formData.placeOfBirth} onChange={handleInputChange} placeholder="City/Province" />
                </label>
              </div>

              <div className="membership-form__fields membership-form__fields--cols-2">
                <label className="membership-form__field">
                  <span>Region</span>
                  <select name="region" value={formData.region} onChange={handleAddressChange}>
                    <option value="">Select Region</option>
                    {Object.keys(ADDRESS_DATA).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </label>
                <label className="membership-form__field">
                  <span>Province</span>
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleAddressChange}
                    disabled={!formData.region}
                    className={!formData.region ? 'field-disabled' : ''}
                  >
                    <option value="">Select Province</option>
                    {formData.region && ADDRESS_DATA[formData.region] && Object.keys(ADDRESS_DATA[formData.region]).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="membership-form__fields membership-form__fields--cols-2">
                <label className="membership-form__field">
                  <span>City / Municipality</span>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleAddressChange}
                    disabled={!formData.province}
                    className={!formData.province ? 'field-disabled' : ''}
                  >
                    <option value="">Select City/Municipality</option>
                    {formData.region && formData.province && ADDRESS_DATA[formData.region][formData.province] && ADDRESS_DATA[formData.region][formData.province].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label className="membership-form__field">
                  <span>Barangay</span>
                  <select
                    name="barangay"
                    value={formData.barangay}
                    onChange={handleAddressChange}
                    disabled={!formData.city}
                    className={!formData.city ? 'field-disabled' : ''}
                  >
                    <option value="">Select Barangay</option>
                    {formData.city && getBarangays(formData.city).map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="membership-form__fields membership-form__fields--cols-2">
                <label className="membership-form__field">
                  <span>House No., Street, Subdivision</span>
                  <input
                    type="text"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    disabled={!formData.barangay}
                     className={!formData.barangay ? 'field-disabled' : ''}
                    placeholder="House No., Street, etc."
                  />
                </label>
                <label className="membership-form__field">
                  <span>Postal Code</span>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    disabled={!formData.barangay}
                     className={!formData.barangay ? 'field-disabled' : ''}
                    placeholder="ZIP Code"
                  />
                </label>
              </div>

              <div className="membership-form__fields membership-form__fields--cols-3">
                <label className="membership-form__field">
                  <span>Phone Number</span>
                   <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="09XX XXX XXXX" />
                </label>
                <label className="membership-form__field">
                  <span>Email</span>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" />
                </label>
                 <label className="membership-form__field">
                  <span>Religion</span>
                  <input type="text" name="religion" value={formData.religion} onChange={handleInputChange} placeholder="Religion" />
                </label>
              </div>

               <div className="membership-form__fields membership-form__fields--cols-2">
                <label className="membership-form__field">
                  <span>Marital Status</span>
                   <select name="maritalStatus" value={formData.maritalStatus} onChange={handleInputChange}>
                       <option value="">Select Status</option>
                       <option value="Single">Single</option>
                       <option value="Married">Married</option>
                       <option value="Widowed">Widowed</option>
                       <option value="Separated">Separated</option>
                   </select>
                </label>
                <label className="membership-form__field">
                  <span>No. of Children</span>
                  <input type="number" name="numberOfChildren" value={formData.numberOfChildren} onChange={handleInputChange} min="0" />
                </label>
              </div>

               <label className="membership-form__field">
                  <span>Educational Attainment</span>
                  <input type="text" name="education" value={formData.education} onChange={handleInputChange} placeholder="Highest degree completed" />
              </label>

              <div className="membership-form__field">
                  <span>Gender</span>
                  <select name="gender" value={formData.gender} onChange={handleInputChange}>
                       <option value="">Select Gender</option>
                       {genderOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                   </select>
              </div>
            </FormSection>

            {/* --- STEP 2: EMPLOYMENT INFORMATION --- */}
            <FormSection title="Employment Information" isActive={currentStep === 2}>
               <div className="membership-form__fields membership-form__fields--cols-2">
                <label className="membership-form__field">
                  <span>Company Name</span>
                  <input type="text" name="company" value={formData.company} onChange={handleInputChange} placeholder="Company name" />
                </label>
                <label className="membership-form__field">
                  <span>Position</span>
                  <input type="text" name="position" value={formData.position} onChange={handleInputChange} placeholder="Job position" />
                </label>
              </div>

               <div className="membership-form__fields membership-form__fields--cols-3">
                <label className="membership-form__field">
                  <span>Years Employed</span>
                  <input type="number" min="0" name="yearsEmployed" value={formData.yearsEmployed} onChange={handleInputChange} placeholder="e.g. 5" />
                </label>
                 <label className="membership-form__field">
                  <span>Union Name</span>
                  <input type="text" name="department" value={formData.department} onChange={handleInputChange} placeholder="Department/Union Name" />
                </label>
                <label className="membership-form__field">
                  <span>Union Affiliation</span>
                  <input type="text" name="unionAffiliation" value={formData.unionAffiliation} onChange={handleInputChange} placeholder="Affiliation" />
                </label>
              </div>

               <div className="membership-form__fields membership-form__fields--cols-2">
                <label className="membership-form__field">
                  <span>Union Position</span>
                  <input type="text" name="unionPosition" value={formData.unionPosition} onChange={handleInputChange} placeholder="Position in Union" />
                </label>
              </div>
            </FormSection>

            {/* --- STEP 3: CONTACT PERSON --- */}
            <FormSection title="Contact Person in Case of Emergency" isActive={currentStep === 3}>
              <div className="membership-form__fields membership-form__fields--cols-2">
                <label className="membership-form__field">
                  <span>Full Name</span>
                  <input type="text" name="emergencyName" value={formData.emergencyName} onChange={handleInputChange} placeholder="Contact person's name" />
                </label>
                 <label className="membership-form__field">
                  <span>Relationship</span>
                  <input type="text" name="emergencyRelationship" value={formData.emergencyRelationship} onChange={handleInputChange} placeholder="e.g. Spouse, Parent" />
                </label>
              </div>

              <div className="membership-form__field">
                <span>Address</span>
                <div className="address-toggle-control" style={{ alignSelf: 'flex-start', marginBottom: '0.5rem' }}>
                  <span className="address-toggle-control__label">Use Primary Address</span>
                  <button
                    type="button"
                    className={`address-toggle${useSameAddress ? " is-active" : ""}`}
                    aria-pressed={useSameAddress}
                    aria-label="Use primary address"
                    onClick={() => {
                      const nextChecked = !useSameAddress;
                      setUseSameAddress(nextChecked);
                      if (nextChecked) {
                        setFormData((prev) => ({ ...prev, emergencyAddress: buildAddressText(prev) }));
                      }
                    }}
                  />
                </div>
                  <textarea
                    rows={2}
                    name="emergencyAddress"
                    value={formData.emergencyAddress}
                    onChange={handleInputChange}
                    placeholder="Contact person's address"
                    disabled={useSameAddress}
                    readOnly={useSameAddress}
                  />
              </div>

              <label className="membership-form__field">
                  <span>Contact Number</span>
                  <input type="tel" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleInputChange} placeholder="Contact person's phone" />
              </label>
            </FormSection>

            {/* --- STEP 4: UPLOAD PHOTO --- */}
            <FormSection title="ID Photo Upload" isActive={currentStep === 4}>
               <div className="photo-upload-container">
                   {photoCropSource ? (
                     <div className="photo-crop-editor">
                       <div
                         ref={photoCropperStageRef}
                         className="photo-cropper-stage"
                         onPointerDown={() => setIsPhotoCropInteracting(true)}
                         onPointerCancel={() => setIsPhotoCropInteracting(false)}
                       >
                         <Cropper
                           image={photoCropSource}
                           crop={photoCrop}
                           cropSize={photoCropSize}
                           zoom={photoZoom}
                           minZoom={photoMinZoom}
                           aspect={1}
                           cropShape="rect"
                           objectFit="cover"
                           restrictPosition
                           showGrid={false}
                           onCropChange={setPhotoCrop}
                           onZoomChange={setPhotoZoom}
                           onCropComplete={handlePhotoCropComplete}
                           onMediaLoaded={handlePhotoMediaLoaded}
                         />
                         <div
                           className={`photo-crop-grid-guide${isPhotoCropInteracting ? " is-visible" : ""}`}
                           aria-hidden="true"
                         />
                       </div>
                       <div className="photo-crop-controls">
                         <label className="photo-crop-zoom-field">
                           <span>Zoom</span>
                           <input
                             type="range"
                             min={photoMinZoom}
                             max={photoMaxZoom}
                             step={0.01}
                             value={photoZoom}
                             onChange={(event) => setPhotoZoom(Number(event.target.value))}
                           />
                         </label>
                         <div className="photo-crop-action-row">
                           <button
                             type="button"
                             className="button-primary"
                             onClick={applyPhotoCrop}
                             disabled={isApplyingPhotoCrop || isValidatingPhotoHuman}
                           >
                             {isValidatingPhotoHuman ? "Validating..." : (isApplyingPhotoCrop ? "Applying..." : "Apply Crop")}
                           </button>
                           <button
                             type="button"
                             className="button-secondary"
                             onClick={() => {
                               setPhotoCropSource(null);
                               setIsPhotoCropInteracting(false);
                               setIsPhotoHumanValidated(false);
                             }}
                           >
                             Cancel
                           </button>
                         </div>
                       </div>
                     </div>
                   ) : (
                     <div className="photo-preview">
                         {photoPreview ? (
                             <img src={photoPreview} alt="Preview" />
                         ) : (
                             <div className="photo-placeholder">
                                 <ImageIcon size={48} />
                                 <span>2x2 Photo</span>
                             </div>
                         )}
                     </div>
                   )}
                   <div className="photo-actions">
                       <p>Please upload then adjust your photo. The system saves it as a square 1:1 ID photo.</p>
                       <label className="button-primary">
                           <Upload size={18} />
                           {photoCropSource ? "Choose Different Photo" : "Choose Photo"}
                           <input type="file" accept="image/*" onChange={handleFileChange} hidden />
                       </label>
                       {photoCropError && <span className="membership-form__error-message">{photoCropError}</span>}
                       {photoPreview && (
                         <button
                           type="button"
                           className="button-outline"
                           onClick={() => {
                             setPhotoCropSource(null);
                             setPhotoCropError("");
                             setIsPhotoCropInteracting(false);
                             setPhotoPreview(null);
                             setCroppedPhotoData(null);
                             setIsPhotoHumanValidated(false);
                           }}
                         >
                           Remove
                         </button>
                       )}
                   </div>
               </div>
            </FormSection>

            {/* --- STEP 5: DIGITAL SIGNATURE --- */}
            <FormSection title="Digital Signature" isActive={currentStep === 5}>
                <p className="signature-instruction">
                    Please sign within the box below. This signature will be used for your Digital ID.
                </p>
                {signatureData ? (
                  <div className="signature-preview-container" style={{ textAlign: 'center', padding: '20px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                     <img 
                       src={signatureData.startsWith('data:') ? signatureData : (signatureData.startsWith('http') ? signatureData : `${BASE_URL}${signatureData}`)} 
                       alt="My Signature" 
                       style={{ maxWidth: '100%', maxHeight: '150px', display: 'block', margin: '0 auto 15px auto' }} 
                     />
                     <button type="button" className="button-outline-sm" onClick={() => setSignatureData(null)}>
                        <RotateCcw size={14} /> Redraw Signature
                     </button>
                  </div>
                ) : (
                  <SignaturePad onSave={setSignatureData} />
                )}
                
                <div className="legal-consent">
                     <p>
                        By clicking submit, I certify that the information provided is true and correct. 
                        I understand that my data will be processed in accordance with the Data Privacy Act.
                     </p>
                </div>
            </FormSection>

            <FormSection title="Review & Confirm" isActive={currentStep === 6}>
              <h3 className="membership-form__section-banner" style={{ marginBottom: "0.75rem" }}>Personal Information</h3>
              <div className="membership-form__review-list">
                {renderReviewRow("Full Name", [formData.firstName, formData.middleInitial, formData.lastName].filter(Boolean).join(" "))}
                {renderReviewRow("Date of Birth", formData.dateOfBirth)}
                {renderReviewRow("Place of Birth", formData.placeOfBirth)}
                {renderReviewRow("Gender", formData.gender === "Other" ? formData.otherGender : formData.gender)}
                {renderReviewRow("Religion", formData.religion)}
                {renderReviewRow("Educational Attainment", formData.education)}
                {renderReviewRow("Marital Status", formData.maritalStatus)}
                {renderReviewRow("No. of Children", formData.numberOfChildren)}
                {renderReviewRow("Email", formData.email)}
                {renderReviewRow("Phone Number", formData.phone)}
                {renderReviewRow("Region", formData.region)}
                {renderReviewRow("Province", formData.province)}
                {renderReviewRow("City / Municipality", formData.city)}
                {renderReviewRow("Barangay", formData.barangay)}
                {renderReviewRow("House No., Street, Subdivision", formData.addressLine1)}
                {renderReviewRow("Postal Code", formData.zipCode)}
              </div>

              <h3 className="membership-form__section-banner" style={{ marginBottom: "0.75rem", marginTop: "1rem" }}>Employment Information</h3>
              <div className="membership-form__review-list">
                {renderReviewRow("Company", formData.company)}
                {renderReviewRow("Position", formData.position)}
                {renderReviewRow("Years Employed", formatYearsEmployed(formData.yearsEmployed))}
                {renderReviewRow("Union Name", formData.department)}
                {renderReviewRow("Union Affiliation", formData.unionAffiliation)}
                {renderReviewRow("Union Position", formData.unionPosition)}
              </div>

              <h3 className="membership-form__section-banner" style={{ marginBottom: "0.75rem", marginTop: "1rem" }}>Emergency Contact</h3>
              <div className="membership-form__review-list">
                {renderReviewRow("Full Name", formData.emergencyName)}
                {renderReviewRow("Relationship", formData.emergencyRelationship)}
                {renderReviewRow("Address", formData.emergencyAddress)}
                {renderReviewRow("Contact Number", formData.emergencyPhone)}
                {renderReviewRow("ID Photo", photoPreview ? "Uploaded" : "Not uploaded")}
                {renderReviewRow("Digital Signature", signatureData ? "Provided" : "Not provided")}
              </div>

              <label className="membership-form__privacy-consent">
                <input
                  type="checkbox"
                  checked={privacyConfirmed}
                  onChange={(event) => setPrivacyConfirmed(event.target.checked)}
                />
                <span className="membership-form__privacy-consent-text">
                  I confirm all information is correct and I consent to the processing of my personal data in accordance with the Data Privacy Act.
                </span>
              </label>
            </FormSection>

            {/* --- FOOTER ACTIONS --- */}
            <div className="step-actions">
                {currentStep > 1 && (
                    <button type="button" className="button-secondary" onClick={handleBack}>
                        Back
                    </button>
                )}
                
                {currentStep < totalSteps && (
                  <button
                    type="button"
                    className="button-primary"
                    onClick={handleNext}
                    disabled={!isStepValid || (currentStep === 4 && (isApplyingPhotoCrop || isValidatingPhotoHuman))}
                  >
                     {currentStep === 5 ? "Review & Confirm" : "Next"} <ArrowRight size={18} />
                    </button>
                )}

                {currentStep === totalSteps && (
                  <button type="button" className="button-primary" onClick={handleSubmit} disabled={isSubmitting || !isStepValid}>
                        {isSubmitting ? "Submitting..." : (isReapplication ? "Resubmit Application" : "Submit Application")} <CheckCircle2 size={18} />
                    </button>
                )}
            </div>

            </form>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

MembershipFormPage.propTypes = {
  user: PropTypes.object.isRequired,
  onLogout: PropTypes.func.isRequired,
};
